import React, { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useDispatch, useSelector } from 'react-redux';
import { useFragment } from 'react-relay/hooks';

import { createStyles, Dialog, FormControlLabel, Icon, Theme, WithStyles, withStyles } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { useFilterMessagesContext } from 'contexts';
import {
  ButtonSquigglies,
  Checkbox,
  Chip,
  DialogConfirmation,
  EnhancedTextField,
  FileUpload,
  SavedTemplateButton,
  Tooltip,
  Typography,
} from 'embeds/components';
import DialogSavedTemplatesSelector from 'embeds/containers/DialogSavedTemplatesSelector';
import { SenderSelector } from 'embeds/HubScreen/components/SenderSelector';
import { actionCreateMessage } from 'embeds/sagas/sagaWatchersMessage';
import { compact, filter, forEach, get, includes, isEmpty, map, trim } from 'lodash';
import { squigglyTemplates } from 'ui-utils/dist/getters/constants/squigglyTemplates';

import { usePrevious } from '@lib/hooks';
import { selectCurrentMasterSettings, selectCurrentUser } from '@lib/selectors';

import { emitTyping, loadIntakeFormsSquigglies } from 'containers/App/actions';
import { makeSelectIntakeFormsSquigglies } from 'containers/App/selectors';
import usePermissions from 'hooks/usePermissions';
import useReduxMutation from 'hooks/useReduxMutation';
import { randomId } from 'services/relayService';
import request from 'utils/request';
import { containsOffensiveWords } from 'utils/validation';

import { ChatInput_EmbedPatient$key } from './__generated__/ChatInput_EmbedPatient.graphql';
import ButtonChatMessageSubmit from './ButtonChatMessageSubmit';
import { useCanZoomSquiggly } from './hooks/useCanZoomSquiggly';
import { PatientContactCustom } from './PopperPatientContactSelect';

export const VISIBILITY_INTERNAL = 'internal';
export const VISIBILITY_PUBLIC = 'public';

const styles = ({
  custom: {
    palette: { colorAccentSunrise, colorAccentLuma, colorSupportSteel },
  },
  palette: {
    common: { white },
    text,
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    actionsButtons: {
      display: 'flex',
      flex: 1,
      flexDirection: 'row-reverse',
      alignItems: 'center',
    },
    actionsContainer: {
      display: 'flex',
      flex: 1,
      paddingLeft: unit * 3,
      paddingRight: unit * 2,
      alignItems: 'center',
    },
    actionsContainerDense: {
      paddingLeft: unit * 2,
    },
    actionsVisibility: {
      display: 'flex',
      flex: 1,
    },
    senderAndReceiverMessage: {
      display: 'flex',
      flex: 1,
    },
    visibilityBtn: {
      marginRight: 0,
    },
    buttonSquigglyIcon: {
      color: text.primary,
    },
    form: {
      backgroundColor: white,
      '&$internal': {
        backgroundColor: `${colorAccentSunrise} !important`,
      },
    },
    input: {
      '&$internal textarea': {
        border: `1px solid ${colorAccentSunrise}`,
      },
      '& textarea': {
        border: `1px solid ${white}`,
      },
      padding: `${unit}px 0px 0px ${unit * 3}px`,
    },
    internal: {},
    previewImage: {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
    chipUploadData: {
      marginRight: unit * 3,
      maxWidth: 173,
      '& span': {
        display: 'inline-block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    chipUploadDataDense: {
      marginRight: unit * 1.5,
      borderRadius: unit / 2,
    },
    secureButton: {
      display: 'initial',
    },
    secureButtonHidden: {
      display: 'none',
    },
    unsubscribedContainer: {
      background: white,
      display: 'inline-block',
      float: 'left',
      padding: `${unit + 2}px ${unit * 3}px`,
      width: '100%',
      transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
    },
    unsubscribedIcon: {
      color: colorAccentLuma,
      float: 'left',
      fontSize: 13,
      lineHeight: '16px',
      marginRight: unit,
      position: 'relative',
    },
    unsubscribedText: {
      color: text.primary,
      display: 'inline-block',
      float: 'left',
      fontSize: 11,
      fontWeight: 500,
      lineHeight: '16px',
    },
    labelInternalDense: {
      marginLeft: (-1 * unit) / 2,
    },
    invalidContactText: {
      cursor: 'not-allowed',
      padding: '10px 0px 30px 24px',
      fontSize: 'inherit',
      color: colorSupportSteel,
    },
  });

const messages = defineMessages({
  tooltipAttachment: {
    id: 'deprecated.fragments.ChatInput.tooltipAttachment',
    defaultMessage: 'Attachment',
  },
  tooltipSecure: {
    id: 'deprecated.fragments.ChatInput.tooltipSecure',
    defaultMessage: 'Secure Message',
  },
  tooltipSquigglies: {
    id: 'deprecated.fragments.ChatInput.tooltipSquigglies',
    defaultMessage: 'Insert Squiggly',
  },
  invalidTermsWarning: {
    id: 'deprecated.fragments.ChatInput.invalidTermsWarning',
    defaultMessage: 'Invalid terms used',
  },
  whiteSpacesWarning: {
    id: 'deprecated.fragments.ChatInput.whiteSpacesWarning',
    defaultMessage: 'You cannot send a blank message',
  },
  invalidContactWarning: {
    id: 'deprecated.fragments.ChatInput.invalidContactWarning',
    defaultMessage: "This patient's active contact method is invalid so you can't message them.",
  },
  invalidContactLearnMore: {
    id: 'deprecated.fragments.ChatInput.invalidContactLearnMore',
    defaultMessage: '[Learn more].',
  },
});

const findSMS = (item: any) => item.active && item.type === 'sms';
const findEmail = (item: any) => item.active && item.type === 'email';
const findWhatsapp = (item: any) => item.active && item.type === 'whatsapp';

let fileUploadRef = createRef<any>();

export interface ChatInputProps extends WithStyles<typeof styles> {
  fragmentRef: ChatInput_EmbedPatient$key;
  variant?: 'regular' | 'dense';
  source?: 'hub' | 'patientProfile';
  shouldHideSenderSelector?: boolean;
}

/**
 * TODO Cleanup legacy code for `ChatInput`
 *
 * @param props
 */
const ChatInput: React.FC<ChatInputProps> = ({
  classes,
  fragmentRef,
  variant = 'regular',
  source = 'hub',
  shouldHideSenderSelector = false,
}) => {
  const patient = useFragment<ChatInput_EmbedPatient$key>(
    graphql`
      fragment ChatInput_EmbedPatient on Patient {
        _id
        createdAt
        updatedAt
        balance
        name
        firstname
        lastname
        doNotContact
        contact {
          active
          type
          value
          archived
        }
        subscribers {
          subscribed
          user
        }
        ...ButtonChatMessageSubmit_EmbedPatient
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const { hasAccessToProduct } = usePermissions();
  const currentMasterSettings = useSelector(selectCurrentMasterSettings);
  const currentUser = useSelector(selectCurrentUser);
  const currentUserId = currentUser?.id;
  const intakeFormsSquigglies = useSelector(makeSelectIntakeFormsSquigglies());
  const { sender } = useFilterMessagesContext();
  const canUseZoomSquiggly = useCanZoomSquiggly();

  const directMessage = (window as any).directMessage;

  const [selectedContacts, setSelectedContacts] = useState<PatientContactCustom[]>();
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState<any>(false);
  const [message, setMessage] = useState<any>(directMessage ? directMessage.message : '');
  const [isWhiteSpacesOnlyMessage, setIsWhiteSpacesOnlyMessage] = useState<boolean>(false);
  const [mentions, setMentions] = useState<any>([]);
  const [patientMessageTemplate, setPatientMessageTemplate] = useState<any>(undefined);
  const [preview, setPreview] = useState<any>();
  const [previewDialogVisible, setPreviewDialogVisible] = useState<any>(false);
  const [selectedTemplateMessage, setSelectedTemplateMessage] = useState<any>(false);
  const [secure, setSecure] = useState<any>(
    // In case setting is missing, always set to true
    get(currentMasterSettings, 'communication.defaultChatCommunicationSecurity.level', 'secure') === 'secure',
  );
  const [uploadData, setUploadData] = useState<any>(false);
  const [visibility, setVisibility] = useState<any>(
    directMessage ? VISIBILITY_PUBLIC : get(currentMasterSettings, 'communication.defaultChatVisibility', 'internal'),
  );
  const [textareaInfo, setTextareaInfo] = useState<any>({});
  const [templateAdded, setTemplateAdded] = useState<any>('');
  const [dialogTemplatesOpen, setDialogTemplatesOpen] = useState(false);
  const [hasOffensiveWord, setHasOffensiveWord] = useState<boolean>(false);
  const [tooltipAttachmentOpen, setTooltipAttachmentOpen] = useState(false);
  const [tooltipSecureMessageOpen, setTooltipSecureMessageOpen] = useState(false);
  const prevPatient = usePrevious(patient);

  const [execute] = useReduxMutation(actionCreateMessage);

  useEffect(() => {
    dispatch(loadIntakeFormsSquigglies());

    return () => {
      (window as any).directMessage = undefined;
    };
  }, [dispatch]);

  useEffect(() => {
    // using id to compare because sometimes object compare returns true when some prop change
    if (prevPatient && patient && prevPatient._id !== patient._id) {
      setVisibility(get(currentMasterSettings, 'communication.defaultChatVisibility', 'internal'));
      setSecure(
        get(currentMasterSettings, 'communication.defaultChatCommunicationSecurity.level', 'secure') === 'secure',
      );
      setUploadData(false);
      setMessage('');
      setMentions([]);
      setPreview(false);
      setPatientMessageTemplate(undefined);
      setSelectedTemplateMessage(false);
    }
  }, [prevPatient, patient, setVisibility, currentMasterSettings]);

  const handleTypingStart = useCallback(() => {
    dispatch(
      emitTyping({
        recipient: patient._id,
        type: 'doctor',
      }),
    );
  }, [dispatch, patient._id]);

  const handleClearRequest = () => {
    setMessage('');
    setPatientMessageTemplate(undefined);
    setSelectedTemplateMessage(false);
  };

  const handleDialogDialogSavedTemplatesSelectorClose = (savedTemplateMessage: any) => {
    setDialogTemplatesOpen(false);
    if (savedTemplateMessage) {
      setMessage(savedTemplateMessage.message);
      setPatientMessageTemplate(savedTemplateMessage._id);
      setSelectedTemplateMessage(savedTemplateMessage);
    }
  };

  const handleFileSubmit = (newUploadData: any) => {
    setUploadData(newUploadData);

    if (/image/i.test(newUploadData.file.type)) {
      const image = new Image();

      image.onload = () => {
        setPreview(
          <img className={classes.previewImage} alt={newUploadData.file.name} src={newUploadData.file.preview} />,
        );
      };

      image.src = newUploadData.file.preview;
    }
  };

  const handleGetCursorPointer = (newTextareaInfo: any) => {
    setTextareaInfo(newTextareaInfo || {});
  };

  const generateHelperTextMessage = () => {
    if (hasOffensiveWord) return formatMessage(messages.invalidTermsWarning);
    if (isWhiteSpacesOnlyMessage) return formatMessage(messages.whiteSpacesWarning);
    return '';
  };

  const checkForWhiteSpacesOnlyMessage = (text: string) => {
    if (!text) return setIsWhiteSpacesOnlyMessage(false);
    if (trim(text) === '' && !uploadData) return setIsWhiteSpacesOnlyMessage(true);
    setIsWhiteSpacesOnlyMessage(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);

    checkForWhiteSpacesOnlyMessage(event.target.value);

    const messageContainsOffensiveWords = containsOffensiveWords(event.target.value);

    setHasOffensiveWord(messageContainsOffensiveWords);

    if (visibility === VISIBILITY_PUBLIC) handleTypingStart();
  };

  const handleMentionsChange = (newMentions: any, newMessage: any) => {
    if (newMentions.length > 0) {
      setVisibility(VISIBILITY_INTERNAL);
    }
    setMentions(newMentions);
    setMessage(newMessage);
  };

  const handleOpenPreviewDialog = () => {
    setPreviewDialogVisible(true);
  };

  const handleOpenUploadDialog = () => {
    // @ts-ignore
    fileUploadRef.open();
  };

  const handleSecureChange = () => {
    if (secure) {
      setConfirmationDialogVisible(true);
    } else {
      setSecure(!secure);
    }
  };

  const handleSquigglyTemplateSelected = (selectedTemplate: any) => {
    const { cursorEnd, cursorStart } = textareaInfo;
    const newValue = `${message.slice(0, cursorStart)} ${selectedTemplate.id} ${message.slice(cursorEnd)}`;

    setMessage(newValue);
    setTemplateAdded(selectedTemplate.id);
  };

  const handleSubmit = useCallback(
    async (event: any) => {
      // stop the form from refreshing the page on submit
      event.preventDefault();

      if (hasOffensiveWord) return;

      // Skip submit if there's no contact selected
      if (selectedContacts && selectedContacts.length === 0) {
        return;
      }

      if (trim(message) === '' && !uploadData) return;

      setSecure(
        get(currentMasterSettings, 'communication.defaultChatCommunicationSecurity.level', 'secure') === 'secure',
      );
      setUploadData(false);
      setMessage('');
      setMentions([]);
      setPreview(false);
      setPatientMessageTemplate(undefined);
      setSelectedTemplateMessage(false);
      setSelectedContacts(undefined);

      const isSenderTheCurrentUser = sender === currentUserId;
      const shouldAddFacility = isSenderTheCurrentUser ? false : sender;

      // call the onSend callback with the message message
      const data: any = {
        recipient: patient._id,
        recipientType: 'patient',
        channel: 'chat',
        mentions,
        ...(shouldAddFacility && { facility: sender }),
        patient: patient._id,
        patientMessageTemplate,
        ref: 'chat',
        secure,
        text: trim(message) || 'file upload',
        type: 'outbound',
        automated: false,
        visibility,
        autoCloseChat:
          selectedTemplateMessage && selectedTemplateMessage.autoCloseChat
            ? selectedTemplateMessage.autoCloseChat
            : false,
        source,
        notifyTo: !isEmpty(selectedContacts)
          ? map(compact(selectedContacts), (selectedContact) => ({
              channel: selectedContact.type,
              to: selectedContact.value,
            }))
          : undefined,
      };

      const fakeId = randomId('Message');
      const actionPayload = { message: data, fakeId, file: uploadData };

      // file upload
      if (uploadData) {
        // WORKAROUND force action request to show file upload pending
        dispatch(actionCreateMessage.request(actionPayload));

        const fileBody: any = new FormData();
        forEach(uploadData, (value, key) => {
          fileBody.append(key, value);
        });
        fileBody.type = 'patient-message';
        fileBody.patient = data.patient;
        fileBody.recipient = data.recipient;
        fileBody.recipientType = data.recipientType;

        const fileEndpoint = '/fileUploads/binary';
        const fileOptions = { method: 'post', body: fileBody };
        const uploadedFile: any = await request(fileEndpoint, fileOptions);
        data.file = uploadedFile._id;
        actionPayload.file = uploadedFile._id;
      }
      execute({ method: 'post', url: '/messages', data }, actionPayload);
    },
    [
      currentUserId,
      dispatch,
      execute,
      currentMasterSettings,
      setSecure,
      setUploadData,
      setMessage,
      setMentions,
      setPreview,
      setPatientMessageTemplate,
      setSelectedTemplateMessage,
      setSelectedContacts,
      mentions,
      message,
      patient._id,
      patientMessageTemplate,
      secure,
      selectedContacts,
      selectedTemplateMessage,
      source,
      uploadData,
      visibility,
      hasOffensiveWord,
      sender,
    ],
  );

  const handleVisibilityChange = ({ target: { checked } }: any) => {
    const disabled =
      patient.doNotContact ||
      !patient.contact ||
      !(patient.contact.find(findSMS) || patient.contact.find(findWhatsapp) || patient.contact.find(findEmail));

    if (!checked && disabled) {
      setMessage('');
    }
    setVisibility(checked ? VISIBILITY_INTERNAL : VISIBILITY_PUBLIC);
  };

  const unsubscribedUsers = map(filter(patient.subscribers, { subscribed: false }), 'user');
  const mentionedUsers = filter(mentions, (item) => includes(unsubscribedUsers, item.refId));

  const hubSquigglies = squigglyTemplates.hub;

  const currentUserAvailableSquigglies = filter(
    hubSquigglies,
    (squiggly) => !squiggly.provisioning || hasAccessToProduct(squiggly.provisioning),
  );

  const availableSquiggles = !intakeFormsSquigglies
    ? currentUserAvailableSquigglies
    : [...currentUserAvailableSquigglies, ...intakeFormsSquigglies];

  const invalidContact = useMemo(
    () =>
      visibility === VISIBILITY_PUBLIC &&
      !patient.doNotContact &&
      patient.contact &&
      patient.contact.find((item) => item?.archived),
    [visibility, patient.doNotContact, patient.contact],
  );

  const disabled =
    visibility === VISIBILITY_PUBLIC &&
    (patient.doNotContact ||
      !patient.contact ||
      !(patient.contact.find(findSMS) || patient.contact.find(findWhatsapp) || patient.contact.find(findEmail)));
  let hintText = visibility === VISIBILITY_PUBLIC ? `Message ${patient.name}` : 'Message your team';

  if (disabled) hintText = 'Collaboration Hub only available for patients with SMS, WhatsApp or email enabled';

  // disable the button to attach a file when the chat is not secure.
  const attachFileButtonDisabled = (!secure && visibility !== VISIBILITY_INTERNAL) || disabled;

  // disable the option to go back to unsecure when there is a file attached.
  const secureChatButtonDisabled = uploadData || disabled;

  return (
    <form
      className={classnames(classes.form, { [classes.internal]: visibility === VISIBILITY_INTERNAL })}
      onSubmit={handleSubmit}
    >
      <DialogConfirmation
        cancelLabel="Reset to secure message"
        label="You acknowledge you are sending a message that does not contain PHI as defined by HIPAA."
        onCancel={() => setConfirmationDialogVisible(false)}
        onSubmit={() => {
          setSecure(false);
          setConfirmationDialogVisible(false);
        }}
        submitLabel="I acknowledge"
        title="Warning!"
        visible={confirmationDialogVisible}
      />
      {mentionedUsers.length !== 0 && (
        <div className={classes.unsubscribedContainer}>
          <Icon className={classes.unsubscribedIcon}>warning</Icon>
          <div className={classes.unsubscribedText}>
            {`
            User${mentionedUsers.length > 1 ? 's' : ''} ${map(mentionedUsers, 'mention').join(', ')} has unsubscribed
            from this patient’s chat and will not be notified about this message
          `}
          </div>
        </div>
      )}
      {invalidContact ? (
        <Typography className={classes.invalidContactText}>
          {formatMessage(messages.invalidContactWarning)}{' '}
          <a href="https://support.lumahealth.io/hc/en-us/articles/360061017151" target="_blank" rel="noreferrer">
            {formatMessage(messages.invalidContactLearnMore)}
          </a>
        </Typography>
      ) : (
        <EnhancedTextField
          className={classnames(classes.input, { [classes.internal]: visibility === VISIBILITY_INTERNAL })}
          variant="chat"
          disabled={Boolean(disabled) || Boolean(invalidContact)}
          externalTemplateAdded={templateAdded}
          hintText={hintText}
          ignoreInvalidSquigglies={Boolean(selectedTemplateMessage)}
          name="messageInput"
          onChange={handleInputChange}
          onInfoUpdated={handleGetCursorPointer}
          onMentionsChange={handleMentionsChange}
          onSubmit={handleSubmit}
          rows={2}
          submitOnEnter
          squigglyTemplates={availableSquiggles}
          value={message}
          cleanAllMentions
          helperText={generateHelperTextMessage()}
          error={hasOffensiveWord || isWhiteSpacesOnlyMessage}
        />
      )}

      <div
        className={classnames(classes.actionsContainer, {
          [classes.actionsContainerDense]: variant === 'dense',
        })}
      >
        <div className={classes.actionsVisibility}>
          <FormControlLabel
            classes={{
              label: variant === 'dense' ? classes.labelInternalDense : undefined,
            }}
            control={
              <Checkbox
                size={variant === 'dense' ? 'small' : undefined}
                checked={visibility === VISIBILITY_INTERNAL}
                disabled={mentions && mentions.length > 0}
                name="visibility"
                onChange={handleVisibilityChange}
                value={VISIBILITY_PUBLIC}
                color="primary"
              />
            }
            label={variant === 'dense' ? 'Internal' : 'Internal message'}
            className={classes.visibilityBtn}
          />
        </div>
        {!shouldHideSenderSelector && <SenderSelector />}
        <div className={classes.actionsButtons}>
          <ButtonChatMessageSubmit
            data-lh-id="ChatInput-btn-submit"
            fragmentRef={patient}
            visibility={visibility}
            type="submit"
            variant="contained"
            color="primary"
            size={variant === 'dense' ? 'small' : undefined}
            selectedContacts={selectedContacts}
            onContactsSelected={(contacts) => setSelectedContacts(contacts ? compact(contacts) : undefined)}
            disabled={
              (!message && !uploadData) ||
              disabled ||
              Boolean(invalidContact) ||
              hasOffensiveWord ||
              isWhiteSpacesOnlyMessage ||
              (message.includes('{{system.url.adhocTelehealth}}') && !canUseZoomSquiggly)
            }
          />
          <Tooltip
            open={tooltipAttachmentOpen}
            title={formatMessage(messages.tooltipAttachment)}
            hideArrow
            placement="top"
          >
            {uploadData ? (
              <Chip
                className={classnames(classes.chipUploadData, {
                  [classes.chipUploadDataDense]: variant === 'dense',
                })}
                color="supportSlate"
                size="small"
                icon={<AttachFileIcon fontSize="small" />}
                label={variant === 'dense' ? undefined : uploadData.file.name}
                onClick={/image/i.test(uploadData.file.type) ? handleOpenPreviewDialog : undefined}
                onDelete={() => setUploadData(false)}
              />
            ) : (
              <IconButton
                data-lh-id="ChatInput-btn-attachment"
                aria-label="Attach File"
                disabled={attachFileButtonDisabled}
                onClick={() => {
                  setTooltipAttachmentOpen(false);
                  handleOpenUploadDialog();
                }}
                onMouseEnter={() => setTooltipAttachmentOpen(true)}
                onMouseLeave={() => setTooltipAttachmentOpen(false)}
              >
                <AttachFileIcon
                  color={!attachFileButtonDisabled ? 'primary' : 'disabled'}
                  fontSize={variant === 'dense' ? 'small' : undefined}
                />
              </IconButton>
            )}
          </Tooltip>
          <Tooltip
            open={tooltipSecureMessageOpen}
            title={formatMessage(messages.tooltipSecure)}
            hideArrow
            placement="top"
          >
            <IconButton
              data-lh-id="ChatInput-btn-secure"
              aria-label="Secure Message"
              className={classnames(classes.secureButton, {
                [classes.secureButtonHidden]: visibility !== VISIBILITY_PUBLIC,
              })}
              disabled={!!secureChatButtonDisabled}
              type="button"
              onClick={() => {
                setTooltipSecureMessageOpen(false);
                handleSecureChange();
              }}
              onMouseEnter={() => setTooltipSecureMessageOpen(true)}
              onMouseLeave={() => setTooltipSecureMessageOpen(false)}
            >
              {secure ? (
                <LockIcon
                  color={!secureChatButtonDisabled ? 'primary' : 'disabled'}
                  fontSize={variant === 'dense' ? 'small' : undefined}
                />
              ) : (
                <LockOpenIcon
                  color={!secureChatButtonDisabled ? 'primary' : 'disabled'}
                  fontSize={variant === 'dense' ? 'small' : undefined}
                />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={formatMessage(messages.tooltipSquigglies)} hideArrow placement="top">
            <ButtonSquigglies
              data-lh-id="ChatInput-btn-squigglies"
              color="primary"
              iconProps={{
                fontSize: variant === 'dense' ? 'small' : undefined,
              }}
              squigglies="hub"
              onSquigglySelected={handleSquigglyTemplateSelected}
              iconMode
            />
          </Tooltip>
          {variant === 'regular' && (
            <SavedTemplateButton
              disabled={disabled}
              iconButtonMode
              onClearRequest={handleClearRequest}
              onClick={() => setDialogTemplatesOpen(true)}
              selectedTemplateMessage={selectedTemplateMessage}
            />
          )}
          <FileUpload
            fileUploadRef={(ref: any) => {
              fileUploadRef = ref;
            }}
            onFileSubmit={handleFileSubmit}
          />
        </div>
      </div>
      {preview && (
        <Dialog open={previewDialogVisible} onClose={() => setPreviewDialogVisible(false)}>
          <div style={{ overflow: 'auto' }}>{preview}</div>
        </Dialog>
      )}
      <DialogSavedTemplatesSelector
        open={dialogTemplatesOpen}
        onClose={handleDialogDialogSavedTemplatesSelectorClose}
        type="chat"
      />
    </form>
  );
};

export default withStyles(styles)(ChatInput);
